import React, { useState, useEffect } from 'react';
import {
  HeartIcon,
  TrophyIcon,
  UserGroupIcon,
  StarIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import DonationForm from '../../components/donations/DonationForm';
import PaymentService from '../../services/paymentService';

const DonationsPage = () => {
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [fundraisingEvents, setFundraisingEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [events, donationStats] = await Promise.all([
        PaymentService.getFundraisingEvents(),
        PaymentService.getDonationStats()
      ]);
      setFundraisingEvents(events.filter(e => e.status === 'active'));
      setStats(donationStats);
    } catch (error) {
      console.error('Error loading donation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const impactStories = [
    {
      title: "New Basketball Uniforms",
      description: "Thanks to your donations, our basketball teams received new uniforms that boost team pride and performance.",
      amount: 2500,
      image: "🏀"
    },
    {
      title: "Weight Room Equipment",
      description: "Community support helped us purchase new strength training equipment for all our athletic programs.",
      amount: 5000,
      image: "💪"
    },
    {
      title: "Student Scholarships",
      description: "Your generosity provided scholarships for students who needed financial assistance to participate in sports.",
      amount: 3500,
      image: "🎓"
    }
  ];

  const donationLevels = [
    {
      name: "Team Supporter",
      amount: 25,
      description: "Help with basic equipment and supplies",
      icon: "⚽",
      benefits: ["Recognition on website", "Thank you email"]
    },
    {
      name: "Athletic Booster",
      amount: 100,
      description: "Support team uniforms and gear",
      icon: "🏆",
      benefits: ["Recognition on website", "Quarterly newsletter", "Event invitations"]
    },
    {
      name: "Championship Sponsor",
      amount: 500,
      description: "Fund major equipment and facility improvements",
      icon: "🥇",
      benefits: ["Recognition plaque", "VIP event access", "Annual report", "Tax receipt"]
    },
    {
      name: "Legacy Partner",
      amount: 1000,
      description: "Create lasting impact on our athletic programs",
      icon: "⭐",
      benefits: ["Permanent recognition", "Exclusive events", "Program naming opportunity", "Direct impact reports"]
    }
  ];

  if (showDonationForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <button
              onClick={() => setShowDonationForm(false)}
              className="inline-flex items-center text-blue-600 hover:text-blue-500"
            >
              <ChevronRightIcon className="h-4 w-4 mr-1 transform rotate-180" />
              Back to Donations
            </button>
          </div>
          <DonationForm />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <HeartIcon className="mx-auto h-16 w-16 mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Support East End Prep Athletics
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Your donation helps provide equipment, facilities, and opportunities that shape the future of our student-athletes.
            </p>
            <button
              onClick={() => setShowDonationForm(true)}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              Donate Now
              <HeartIcon className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Impact Together</h2>
              <p className="text-lg text-gray-600">
                See how your support is making a difference in our athletic programs
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <TrophyIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {PaymentService.formatCurrency(stats.totalAmount)}
                </div>
                <div className="text-gray-600">Total Raised</div>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <UserGroupIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {stats.totalDonors.toLocaleString()}
                </div>
                <div className="text-gray-600">Generous Donors</div>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <StarIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {stats.programsSupported || 12}
                </div>
                <div className="text-gray-600">Programs Supported</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Fundraising Events */}
      {fundraisingEvents.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Current Fundraising Goals</h2>
              <p className="text-lg text-gray-600">
                Help us reach these important milestones for our athletic programs
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {fundraisingEvents.map((event) => (
                <div key={event.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{event.name}</h3>
                    <p className="text-gray-600 mb-4">{event.description}</p>
                    
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{Math.round((event.raised / event.goal) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min((event.raised / event.goal) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <div className="text-2xl font-bold text-gray-900">
                          {PaymentService.formatCurrency(event.raised)}
                        </div>
                        <div className="text-sm text-gray-500">
                          of {PaymentService.formatCurrency(event.goal)} goal
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">{event.donors}</div>
                        <div className="text-sm text-gray-500">donors</div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setShowDonationForm(true)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Support This Goal
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Donation Levels */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ways to Support</h2>
            <p className="text-lg text-gray-600">
              Choose a donation level that works for you - every contribution makes a difference
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {donationLevels.map((level) => (
              <div key={level.name} className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-blue-500 transition-colors duration-200">
                <div className="text-center mb-4">
                  <div className="text-4xl mb-2">{level.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900">{level.name}</h3>
                  <div className="text-3xl font-bold text-blue-600 mt-2">
                    {PaymentService.formatCurrency(level.amount)}
                  </div>
                </div>
                <p className="text-gray-600 text-center mb-4">{level.description}</p>
                <ul className="space-y-2 mb-6">
                  {level.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowDonationForm(true)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors duration-200"
                >
                  Donate {PaymentService.formatCurrency(level.amount)}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Impact Stories */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Impact in Action</h2>
            <p className="text-lg text-gray-600">
              See how donations have directly improved our athletic programs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactStories.map((story, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="text-center mb-4">
                    <div className="text-6xl mb-4">{story.image}</div>
                    <h3 className="text-xl font-bold text-gray-900">{story.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{story.description}</p>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {PaymentService.formatCurrency(story.amount)}
                    </div>
                    <div className="text-sm text-gray-500">Community Investment</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4">Ready to Make a Difference?</h2>
          <p className="text-xl mb-8">
            Join our community of supporters and help East End Prep student-athletes reach their full potential.
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <button
              onClick={() => setShowDonationForm(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              Make a Donation
              <HeartIcon className="ml-2 h-5 w-5" />
            </button>
            <a
              href="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-blue-600 transition-colors duration-200"
            >
              Learn More
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationsPage;